import { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

// Keep an in-memory token too — some iOS Safari configurations drop third-party
// SameSite=None cookies. We still send cookies first, header as a fallback.
const TOKEN_KEY = "pl_user_token";
export const getStoredToken = () => {
  try { return localStorage.getItem(TOKEN_KEY) || ""; } catch { return ""; }
};
const setStoredToken = (t) => {
  try {
    if (t) localStorage.setItem(TOKEN_KEY, t);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {}
};

// Axios instance pre-wired with credentials + bearer fallback.
export const authAxios = axios.create({ withCredentials: true });
authAxios.interceptors.request.use((cfg) => {
  const t = getStoredToken();
  if (t) {
    cfg.headers = cfg.headers || {};
    if (!cfg.headers.Authorization) cfg.headers.Authorization = `Bearer ${t}`;
  }
  return cfg;
});

const formatDetail = (detail) => {
  if (detail == null) return "حدث خطأ، حاول مرة أخرى";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  try { return JSON.stringify(detail); } catch { return "خطأ غير معروف"; }
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await authAxios.get(`${API}/auth/me`);
      // Mirror the current session token into localStorage so Bearer-fallback
      // keeps working even if the browser later drops the cookie (iOS Safari).
      if (data?.session_token) setStoredToken(data.session_token);
      // Strip the token from the user object so the rest of the app treats it as data.
      const { session_token, ...userOnly } = data || {};
      setUser(userOnly);
      return userOnly;
    } catch {
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Skip the auto /me check if an OAuth callback hash is present —
  // AuthCallback will resolve it first.
  useEffect(() => {
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    try {
      const { data } = await authAxios.post(`${API}/auth/login`, { email, password });
      if (data.session_token) setStoredToken(data.session_token);
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatDetail(e.response?.data?.detail) || e.message };
    }
  };

  const register = async (email, password, name) => {
    try {
      const { data } = await authAxios.post(`${API}/auth/register`, { email, password, name });
      if (data.session_token) setStoredToken(data.session_token);
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatDetail(e.response?.data?.detail) || e.message };
    }
  };

  const loginWithGoogleSession = async (session_id) => {
    try {
      const { data } = await authAxios.post(`${API}/auth/google`, { session_id });
      if (data.session_token) setStoredToken(data.session_token);
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatDetail(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try { await authAxios.post(`${API}/auth/logout`); } catch {}
    setStoredToken("");
    setUser(null);
  };

  const value = { user, loading, login, register, logout, loginWithGoogleSession, refresh };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export const startGoogleLogin = () => {
  const redirectUrl = window.location.origin + "/auth/callback";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};
